/**
 * Capitalize the first letter of the string
 * @private
 * @param {string} word
 * @return {string}
 */
function capitalize(word = '') {
    return word.charAt(0).toUpperCase() + word.slice(1);
}
/**
 * Whether the data record has columns present
 * @private
 * @param {data-references.DataRecord} dataRecord
 * @return {boolean}
 */
function hasColumns(dataRecord) {
    var _a;
    return ((_a = dataRecord.columns) === null || _a === void 0 ? void 0 : _a.length) > 0;
}
/**
 * A citation for the provided record (column).
 * @param {references.data-references.DataRecord} dataRecord
 * @param {boolean} forDenominator
 * @return {string}
 */
export function getSourceCitation(dataRecord, forDenominator = false) {
    let citation = '';
    if (!hasColumns(dataRecord))
        return citation;
    const index = forDenominator && dataRecord.isNormalized ? 1 : 0;
    const column = dataRecord.columns[index];
    citation = `${column.attribution.sourceName} ${column.timeElement.friendlyShortName}`;
    return citation;
}
/**
 * A citation for the provided records (columns).
 * @param {references.data-references.DataRecord[]} dataRecords
 * @param {string} prefix
 * @return {string}
 */
export function getCitationList(dataRecords, prefix = '') {
    const cite = (dataRecord) => getSourceCitation(dataRecord);
    const sources = dataRecords.map(cite);
    const unique = new Set(sources);
    return [prefix, [...unique.values()].join('; ')].join(' ');
}
/**
 * A link to the catalog page for the provided record (column).
 * @param {references.data-references.DataRecord} dataRecord
 * @param {boolean} forDenominator
 * @return {null|string}
 */
export function getCatalogURL(dataRecord, forDenominator = false) {
    let link = null;
    if (!hasColumns(dataRecord))
        return link;
    const index = forDenominator && dataRecord.isNormalized ? 1 : 0;
    const column = dataRecord.columns[index];
    if (!column.id)
        return link;
    const origin = getCatalogOrigin();
    link = `${origin}/columns/${column.id}/`;
    return link;
}
/**
 * Retrieve the domain for catalog URLs.
 * @return {string}
 */
export function getCatalogOrigin() {
    if (!window)
        return '';
    // Of "data.mysidewalk.com" or "mysidewalk.com"; take last two parts
    const root = window.location.hostname.split('.').slice(-2).join('.');
    const protocol = window.location.protocol;
    return `${protocol}//catalog.${root}`;
}
/**
 * A summary of the normalization
 * @param {references.data-references.DataRecord} dataRecord
 * @return {string}
 */
export function getNormalizationSummary(dataRecord) {
    let summary = '';
    if (!hasColumns(dataRecord))
        return summary;
    if (dataRecord.isNormalized) {
        const column = dataRecord.columns[1];
        const denominator = getCharacteristicTypeName(column);
        summary = `By ${denominator}`;
    }
    else if (dataRecord.canBeNormalized) {
        summary = 'Not Normalized';
    }
    else {
        summary = `${capitalize(dataRecord.statisticalType)} Data`;
    }
    return summary;
}
/**
 * @param {references.system-metadata.Column} column
 * @return {null|string}
 */
export function getCharacteristicTypeName(column) {
    var _a;
    return column ? (_a = column.characteristicType) === null || _a === void 0 ? void 0 : _a.friendlyLongName : null;
}
/**
 * @memberof references.data-references
 */
const DataRecordUtils = {
    getSourceCitation,
    getCitationList,
    getCatalogURL,
    getCatalogOrigin,
    getNormalizationSummary,
    getCharacteristicTypeName,
};
export default DataRecordUtils;
